import { createFileRoute } from "@tanstack/react-router";
import { useState, useEffect } from "react";
import { AppShell } from "@/components/app-shell";
import {
  DCard,
  SectionHeader,
  Pill,
  ProgressBar,
  HeroCard,
  Confetti,
  EmptyState,
  LoadError,
} from "@/components/dryvn";
import { AddPanel, LifeField, LifeInput, LifeSelect, SubmitButton } from "@/components/life-forms";
import { CollapsibleList, ListSearch, useListFilter } from "@/components/collapsible-list";
// Screen migration (2026-07-05): data via adapter-backed hooks; formatCAD
// stays (pure formatter).
// 2026-07-15: web-entry forms (expense/account/budget) + honest empty/error.
// 2026-07-15: Spending un-gated for live + Accounts/Budgets live read-back
// (founder mandate — work the money hub without switching apps). The three
// GETs feed live reads; a web-logged expense/account/budget invalidates its
// read key and reappears on refetch (no fixtures posing as real data).
import { formatCAD } from "@/data/mock";
import {
  useBills,
  useGoals,
  useSpending,
  useAccounts,
  useBudgets,
  useAddTransaction,
  useAddAccount,
  useAddBudget,
} from "@/lib/queries";

export const Route = createFileRoute("/money")({
  head: () => ({
    meta: [
      { title: "Money — Dryvn Life" },
      {
        name: "description",
        content: "Bills, subscriptions, spending, and personal savings goals.",
      },
    ],
  }),
  component: Money,
});

type Tab = "bills" | "spending" | "accounts" | "budgets" | "goals";
type BillsFilter = "all" | "bills" | "subs";

const toCents = (dollars: string) => Math.round(parseFloat(dollars) * 100);

function Money() {
  const { data: bills, isError: billsError, error: billsErr } = useBills();
  const { data: goals, isError: goalsError, error: goalsErr } = useGoals();
  const { data: spending, isError: spendingError, error: spendingErr } = useSpending();
  const { data: accounts, isError: accountsError, error: accountsErr } = useAccounts();
  const { data: budgets, isError: budgetsError, error: budgetsErr } = useBudgets();
  const addTransaction = useAddTransaction();
  const addAccount = useAddAccount();
  const addBudget = useAddBudget();
  const [tab, setTab] = useState<Tab>("bills");
  const [filter, setFilter] = useState<BillsFilter>("all");
  const [celebrated, setCelebrated] = useState(false);

  // Expense form
  const [expAmount, setExpAmount] = useState("");
  const [expCategory, setExpCategory] = useState("");
  const [expMerchant, setExpMerchant] = useState("");
  const [expSaved, setExpSaved] = useState(false);
  const [expError, setExpError] = useState(false);
  // Account form
  const [accName, setAccName] = useState("");
  const [accKind, setAccKind] = useState("chequing");
  const [accBalance, setAccBalance] = useState("");
  const [accSaved, setAccSaved] = useState(false);
  const [accError, setAccError] = useState(false);
  // Budget form
  const [budCategory, setBudCategory] = useState("");
  const [budMonthly, setBudMonthly] = useState("");
  const [budSaved, setBudSaved] = useState(false);
  const [budError, setBudError] = useState(false);

  useEffect(() => {
    if (tab === "goals" && !celebrated) {
      const t = setTimeout(() => setCelebrated(true), 1600);
      return () => clearTimeout(t);
    }
  }, [tab, celebrated]);

  // The success note and input reset only fire once the mutation actually
  // lands (onSuccess); a failure surfaces an honest note and preserves what
  // was typed so it can be retried.
  const submitExpense = () => {
    const dollars = parseFloat(expAmount);
    if (Number.isNaN(dollars) || dollars <= 0) return;
    setExpSaved(false);
    setExpError(false);
    // amountCents is SIGNED; an expense is negative (backend contract).
    addTransaction.mutate(
      {
        amountCents: -toCents(expAmount),
        category: expCategory.trim() || undefined,
        merchant: expMerchant.trim() || undefined,
      },
      {
        onSuccess: () => {
          setExpAmount("");
          setExpCategory("");
          setExpMerchant("");
          setExpSaved(true);
        },
        onError: () => setExpError(true),
      },
    );
  };

  const submitAccount = () => {
    const name = accName.trim();
    if (!name) return;
    setAccSaved(false);
    setAccError(false);
    addAccount.mutate(
      {
        name,
        accountKind: accKind,
        ...(accBalance.trim() && !Number.isNaN(parseFloat(accBalance))
          ? { balanceCents: toCents(accBalance) }
          : {}),
      },
      {
        onSuccess: () => {
          setAccName("");
          setAccBalance("");
          setAccSaved(true);
        },
        onError: () => setAccError(true),
      },
    );
  };

  const submitBudget = () => {
    const category = budCategory.trim();
    const dollars = parseFloat(budMonthly);
    if (!category || Number.isNaN(dollars) || dollars < 0) return;
    setBudSaved(false);
    setBudError(false);
    addBudget.mutate(
      { category, monthlyCents: toCents(budMonthly) },
      {
        onSuccess: () => {
          setBudCategory("");
          setBudMonthly("");
          setBudSaved(true);
        },
        onError: () => setBudError(true),
      },
    );
  };

  const savedNote = (text: string) => (
    <p className="mt-2 text-xs" style={{ color: "var(--dryvn-primary-deep)" }}>
      {text}
    </p>
  );

  const failedNote = (text: string) => (
    <p className="mt-2 text-xs" style={{ color: "var(--dryvn-warm-deep)" }}>
      {text}
    </p>
  );

  const SAVE_FAILED = "We couldn't save that just now — give it another try in a moment.";

  const monthTotal = bills.reduce((s, b) => s + b.amountCents, 0);
  const subs = bills.filter((b) => b.subscription);
  const subsAnnualCents = subs.reduce((s, b) => s + b.amountCents * 12, 0);

  const {
    query: billQuery,
    setQuery: setBillQuery,
    results: billMatches,
    active: billSearchActive,
  } = useListFilter(bills, (b) => [b.name]);

  const filtered =
    filter === "subs"
      ? billMatches.filter((b) => b.subscription)
      : filter === "bills"
        ? billMatches.filter((b) => !b.subscription)
        : billMatches;
  const unpaid = filtered.filter((b) => !b.paid);
  const paid = filtered.filter((b) => b.paid);

  return (
    <AppShell>
      <HeroCard
        eyebrow="Money"
        title={
          <>
            What's{" "}
            <em className="italic" style={{ color: "var(--dryvn-primary-deep)" }}>
              due
            </em>
            , what's saved.
          </>
        }
      >
        <p className="text-sm" style={{ color: "var(--dryvn-ink)" }}>
          <span className="font-mono" style={{ color: "var(--dryvn-primary-deep)" }}>
            {formatCAD(monthTotal)}
          </span>{" "}
          this month <span className="mx-1 opacity-40">·</span> {formatCAD(subsAnnualCents)}/yr on
          subscriptions.
        </p>
      </HeroCard>

      {/* Web entry — log money without texting. Bills & goals have no web
          create path yet (SMS only); these three do. */}
      <div className="mt-6 grid gap-4 lg:grid-cols-3">
        <AddPanel label="Log an expense">
          {() => (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                submitExpense();
              }}
            >
              <LifeField label="Amount ($)">
                <LifeInput
                  type="number"
                  step="0.01"
                  min="0"
                  value={expAmount}
                  onChange={(e) => {
                    setExpAmount(e.target.value);
                    setExpSaved(false);
                  }}
                  placeholder="40.00"
                />
              </LifeField>
              <LifeField label="Category (optional)">
                <LifeInput
                  value={expCategory}
                  onChange={(e) => setExpCategory(e.target.value)}
                  placeholder="Gas, groceries…"
                />
              </LifeField>
              <LifeField label="Where (optional)">
                <LifeInput
                  value={expMerchant}
                  onChange={(e) => setExpMerchant(e.target.value)}
                  placeholder="Merchant"
                />
              </LifeField>
              <SubmitButton disabled={!expAmount.trim()}>Log expense</SubmitButton>
              {expSaved && savedNote("Logged. It feeds your spending picture as entries add up.")}
              {expError && failedNote(SAVE_FAILED)}
            </form>
          )}
        </AddPanel>

        <AddPanel label="Add an account">
          {() => (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                submitAccount();
              }}
            >
              <LifeField label="Name">
                <LifeInput
                  value={accName}
                  onChange={(e) => {
                    setAccName(e.target.value);
                    setAccSaved(false);
                  }}
                  placeholder="Everyday chequing"
                />
              </LifeField>
              <LifeField label="Type">
                <LifeSelect value={accKind} onChange={(e) => setAccKind(e.target.value)}>
                  {[
                    "chequing",
                    "savings",
                    "credit",
                    "loan",
                    "mortgage",
                    "investment",
                    "cash",
                    "other",
                  ].map((k) => (
                    <option key={k} value={k}>
                      {k}
                    </option>
                  ))}
                </LifeSelect>
              </LifeField>
              <LifeField label="Balance ($, optional)">
                <LifeInput
                  type="number"
                  step="0.01"
                  value={accBalance}
                  onChange={(e) => setAccBalance(e.target.value)}
                  placeholder="0.00"
                />
              </LifeField>
              <SubmitButton disabled={!accName.trim()}>Add account</SubmitButton>
              {accSaved && savedNote("Saved to your accounts.")}
              {accError && failedNote(SAVE_FAILED)}
            </form>
          )}
        </AddPanel>

        <AddPanel label="Set a budget">
          {() => (
            <form
              className="space-y-3"
              onSubmit={(e) => {
                e.preventDefault();
                submitBudget();
              }}
            >
              <LifeField label="Category">
                <LifeInput
                  value={budCategory}
                  onChange={(e) => {
                    setBudCategory(e.target.value);
                    setBudSaved(false);
                  }}
                  placeholder="Groceries"
                />
              </LifeField>
              <LifeField label="Monthly limit ($)">
                <LifeInput
                  type="number"
                  step="0.01"
                  min="0"
                  value={budMonthly}
                  onChange={(e) => setBudMonthly(e.target.value)}
                  placeholder="600.00"
                />
              </LifeField>
              <SubmitButton disabled={!budCategory.trim() || !budMonthly.trim()}>
                Save budget
              </SubmitButton>
              {budSaved && savedNote("Budget saved.")}
              {budError && failedNote(SAVE_FAILED)}
            </form>
          )}
        </AddPanel>
      </div>

      <div
        className="mt-8 mb-6 inline-flex rounded-full p-1"
        style={{ backgroundColor: "var(--dryvn-page-tint)" }}
      >
        {[
          { id: "bills" as const, label: "Bills" },
          { id: "spending" as const, label: "Spending" },
          { id: "accounts" as const, label: "Accounts" },
          { id: "budgets" as const, label: "Budgets" },
          { id: "goals" as const, label: "Goals" },
        ].map((t) => (
          <button
            key={t.id}
            onClick={() => setTab(t.id)}
            className="inline-flex items-center gap-2 rounded-full px-4 py-1.5 text-sm transition-colors"
            style={{
              backgroundColor: tab === t.id ? "var(--dryvn-card)" : "transparent",
              color: tab === t.id ? "var(--dryvn-ink)" : "var(--dryvn-grey)",
              boxShadow: tab === t.id ? "var(--shadow-soft)" : "none",
              fontWeight: tab === t.id ? 500 : 400,
            }}
          >
            {t.label}
          </button>
        ))}
      </div>

      {tab === "bills" && (
        <div className="space-y-5">
          <div className="flex flex-wrap gap-2">
            {[
              { id: "all" as const, label: "All" },
              { id: "bills" as const, label: "Bills" },
              { id: "subs" as const, label: "Subscriptions" },
            ].map((c) => (
              <button
                key={c.id}
                onClick={() => setFilter(c.id)}
                className="rounded-full border px-3 py-1 text-xs transition-colors"
                style={{
                  borderColor: filter === c.id ? "var(--dryvn-primary)" : "var(--dryvn-line)",
                  color: filter === c.id ? "var(--dryvn-primary-deep)" : "var(--dryvn-grey)",
                  backgroundColor:
                    filter === c.id
                      ? "color-mix(in oklab, var(--dryvn-primary) 8%, transparent)"
                      : "var(--dryvn-card)",
                }}
              >
                {c.label}
              </button>
            ))}
          </div>

          {billsError && <LoadError error={billsErr} />}
          {!billsError && bills.length === 0 && (
            <EmptyState
              title="No bills tracked yet. Dryvn learns your bills from what you forward or text — try one to start."
              smsExamples={["hydro bill $85 due the 15th", "netflix $17 monthly"]}
            />
          )}

          {filter === "subs" && subs.length > 0 && (
            <DCard
              className="flex items-baseline justify-between"
              style={{ backgroundImage: "var(--grad-warm)" }}
            >
              <div>
                <p
                  className="font-mono text-[10px] uppercase tracking-wider"
                  style={{ color: "var(--dryvn-warm-deep)" }}
                >
                  Subscriptions
                </p>
                <p className="mt-1 font-display text-3xl" style={{ color: "var(--dryvn-ink)" }}>
                  {formatCAD(subsAnnualCents)}
                  <span className="text-base opacity-60">/yr</span>
                </p>
              </div>
              <p className="text-xs" style={{ color: "var(--dryvn-warm-deep)" }}>
                {subs.length} recurring items
              </p>
            </DCard>
          )}

          {bills.length > 0 && (
            <ListSearch
              value={billQuery}
              onChange={setBillQuery}
              placeholder="Search bills…"
              label="Search bills by name"
              className="max-w-sm"
            />
          )}

          {billSearchActive && filtered.length === 0 && (
            <DCard>
              <p className="text-sm" style={{ color: "var(--dryvn-grey)" }}>
                No bills match that search.
              </p>
            </DCard>
          )}

          {unpaid.length > 0 && (
            <section>
              <SectionHeader title="Unpaid" />
              <CollapsibleList items={unpaid} resource="unpaid bills" filtered={billSearchActive}>
                {(shown) => (
                  <DCard>
                    <ul className="divide-y" style={{ borderColor: "var(--dryvn-line)" }}>
                      {shown.map((b) => (
                        <li
                          key={b.id}
                          className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                        >
                          <div>
                            <p className="font-medium" style={{ color: "var(--dryvn-ink)" }}>
                              {b.name}
                            </p>
                            <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                              Day {b.dueDay} · in {b.daysUntil}d
                              {b.subscription && <span> · recurring</span>}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-sm" style={{ color: "var(--dryvn-ink)" }}>
                              {formatCAD(b.amountCents)}
                            </p>
                            {b.daysUntil <= 2 && <Pill tone="amber">soon</Pill>}
                          </div>
                        </li>
                      ))}
                    </ul>
                  </DCard>
                )}
              </CollapsibleList>
            </section>
          )}

          {paid.length > 0 && (
            <section>
              <SectionHeader title="Paid this month" />
              <CollapsibleList items={paid} resource="paid bills" filtered={billSearchActive}>
                {(shown) => (
                  <DCard>
                    <ul className="divide-y" style={{ borderColor: "var(--dryvn-line)" }}>
                      {shown.map((b) => (
                        <li
                          key={b.id}
                          className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                        >
                          <div>
                            <p className="font-medium" style={{ color: "var(--dryvn-ink)" }}>
                              {b.name}
                            </p>
                            <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                              Day {b.dueDay}
                            </p>
                          </div>
                          <div className="text-right">
                            <p className="font-mono text-sm" style={{ color: "var(--dryvn-grey)" }}>
                              {formatCAD(b.amountCents)}
                            </p>
                            <Pill tone="primary">paid</Pill>
                          </div>
                        </li>
                      ))}
                    </ul>
                  </DCard>
                )}
              </CollapsibleList>
            </section>
          )}
        </div>
      )}

      {tab === "spending" && (
        <div className="space-y-5">
          {spendingError && <LoadError error={spendingErr} />}
          {!spendingError && spending.recentLog.length === 0 && (
            <EmptyState
              title="Nothing logged yet — text a spend or use Log expense above, and your spending picture builds here."
              smsExamples={["spent $40 on gas", "groceries $85 at Loblaws"]}
            />
          )}

          {!spendingError && spending.recentLog.length > 0 && (
            <>
              <DCard>
                <p
                  className="font-mono text-[10px] uppercase tracking-wider"
                  style={{ color: "var(--dryvn-grey)" }}
                >
                  {spending.monthLabel}
                </p>
                <p className="mt-1 font-display text-3xl" style={{ color: "var(--dryvn-ink)" }}>
                  {formatCAD(spending.totalCents)}
                </p>
                <p className="mt-2 text-xs italic" style={{ color: "var(--dryvn-grey)" }}>
                  Just the picture. We show where things stand — no judgment.
                </p>
              </DCard>

              {spending.rows.length > 0 && (
                <section>
                  <SectionHeader title="By category" />
                  <DCard>
                    <ul className="space-y-3">
                      {spending.rows.map((r) => (
                        <li key={r.category}>
                          <div className="mb-1 flex justify-between text-sm">
                            <span style={{ color: "var(--dryvn-ink)" }}>{r.category}</span>
                            <span className="font-mono" style={{ color: "var(--dryvn-grey)" }}>
                              {formatCAD(r.amountCents)}
                            </span>
                          </div>
                          <ProgressBar value={r.pct} max={100} tint="life" />
                        </li>
                      ))}
                    </ul>
                  </DCard>
                </section>
              )}

              <section>
                <SectionHeader title="Recent log" />
                <DCard>
                  <ul className="divide-y" style={{ borderColor: "var(--dryvn-line)" }}>
                    {spending.recentLog.map((r) => (
                      <li
                        key={r.id}
                        className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                      >
                        <div>
                          <p className="text-sm" style={{ color: "var(--dryvn-ink)" }}>
                            {r.label}
                          </p>
                          <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                            {r.when}
                          </p>
                        </div>
                        <p className="font-mono text-sm" style={{ color: "var(--dryvn-ink)" }}>
                          {formatCAD(r.amountCents)}
                        </p>
                      </li>
                    ))}
                  </ul>
                </DCard>
              </section>
            </>
          )}
        </div>
      )}

      {tab === "accounts" && (
        <div className="space-y-5">
          {accountsError && <LoadError error={accountsErr} />}
          {!accountsError && accounts.length === 0 && (
            <EmptyState
              title="No accounts yet. Add one above, or text a balance to your Dryvn number to start your net-worth picture."
              smsExamples={["chequing balance $2400", "add savings account $10000"]}
            />
          )}
          {!accountsError && accounts.length > 0 && (
            <section>
              <SectionHeader title="Accounts" />
              <CollapsibleList items={accounts} resource="accounts">
                {(shown) => (
                  <DCard>
                    <ul className="divide-y" style={{ borderColor: "var(--dryvn-line)" }}>
                      {shown.map((a) => (
                        <li
                          key={a.id}
                          className="flex items-center justify-between py-3 first:pt-0 last:pb-0"
                        >
                          <div>
                            <p className="font-medium" style={{ color: "var(--dryvn-ink)" }}>
                              {a.name}
                            </p>
                            <p className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                              {a.accountKind}
                              {a.institution ? ` · ${a.institution}` : ""}
                            </p>
                          </div>
                          <p
                            className="font-mono text-sm"
                            style={{
                              color:
                                a.balanceCents < 0 ? "var(--dryvn-warm-deep)" : "var(--dryvn-ink)",
                            }}
                          >
                            {formatCAD(a.balanceCents)}
                          </p>
                        </li>
                      ))}
                    </ul>
                  </DCard>
                )}
              </CollapsibleList>
            </section>
          )}
        </div>
      )}

      {tab === "budgets" && (
        <div className="space-y-5">
          {budgetsError && <LoadError error={budgetsErr} />}
          {!budgetsError && budgets.length === 0 && (
            <EmptyState
              title="No budgets set. Set one above, or text a monthly cap to your Dryvn number and we'll track spend against it."
              smsExamples={["budget groceries $600 a month", "set dining budget $200"]}
            />
          )}
          {!budgetsError && budgets.length > 0 && (
            <section>
              <SectionHeader title="Monthly budgets" />
              <CollapsibleList items={budgets} resource="budgets">
                {(shown) => (
                  <DCard>
                    <ul className="space-y-4">
                      {shown.map((b) => {
                        const spent = b.spentThisMonthCents ?? 0;
                        const over = spent > b.monthlyCents;
                        return (
                          <li key={b.id}>
                            <div className="mb-1 flex justify-between text-sm">
                              <span style={{ color: "var(--dryvn-ink)" }}>{b.category}</span>
                              <span
                                className="font-mono"
                                style={{
                                  color: over ? "var(--dryvn-warm-deep)" : "var(--dryvn-grey)",
                                }}
                              >
                                {formatCAD(spent)} / {formatCAD(b.monthlyCents)}
                              </span>
                            </div>
                            <ProgressBar
                              value={spent}
                              max={b.monthlyCents || 1}
                              tint={over ? "warm" : "life"}
                            />
                          </li>
                        );
                      })}
                    </ul>
                  </DCard>
                )}
              </CollapsibleList>
            </section>
          )}
        </div>
      )}

      {tab === "goals" && (
        <div className="space-y-4">
          {goalsError && <LoadError error={goalsErr} />}
          {!goalsError && goals.length === 0 && (
            <EmptyState
              title="No savings goals yet. Name one by texting your Dryvn number and watch it fill up here."
              smsExamples={["saving $3000 for a trip by December", "goal: emergency fund $5000"]}
            />
          )}
          <CollapsibleList items={goals} resource="goals">
            {(shown) => (
              <div className="space-y-4">
                {shown.map((g) => {
                  const hit = g.progress >= g.target;
                  return (
                    <DCard key={g.id} className="relative overflow-hidden">
                      {hit && <Confetti show={!celebrated} />}
                      <div className="mb-2 flex items-baseline justify-between">
                        <h3 className="font-display text-lg" style={{ color: "var(--dryvn-ink)" }}>
                          {g.name}
                        </h3>
                        {hit ? (
                          <Pill tone="primary">🎉 Goal reached</Pill>
                        ) : (
                          <span className="text-xs" style={{ color: "var(--dryvn-grey)" }}>
                            by {g.targetDate}
                          </span>
                        )}
                      </div>
                      <ProgressBar
                        value={g.progress}
                        max={g.target}
                        tint={hit ? "primary" : "life"}
                      />
                      <div
                        className="mt-2 flex justify-between text-xs"
                        style={{ color: "var(--dryvn-grey)" }}
                      >
                        <span>
                          <span className="font-mono" style={{ color: "var(--dryvn-ink)" }}>
                            ${g.progress.toLocaleString()}
                          </span>{" "}
                          / ${g.target.toLocaleString()}
                        </span>
                        <span>{Math.round((g.progress / g.target) * 100)}%</span>
                      </div>
                    </DCard>
                  );
                })}
              </div>
            )}
          </CollapsibleList>
        </div>
      )}
    </AppShell>
  );
}
